// @oasis-permission-inherits: /caption-templates (ai-edit-v2-tool:view)
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Loader2, RefreshCw } from "lucide-react";
import { useState, type ReactNode } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import {
  CaptionTemplateColors,
  CaptionTemplateStatusBadge,
  CopyTemplateIdButton,
} from "~/modules/caption-templates/components";
import { CaptionTemplateEditorPanel } from "~/modules/caption-templates/editor-panel";
import {
  formatCaptionTemplateAudienceSegment,
  getCaptionTemplateFilterParams,
} from "~/modules/caption-templates/filters";
import {
  captionTemplateMetadataQuery,
  startCaptionTemplatePreviewAssetsMutation,
} from "~/modules/caption-templates/queries";
import {
  captionTemplateAudienceSchema,
  captionTemplateCategorySchema,
  captionTemplateStatusFilterSchema,
  type CaptionTemplateMetadata,
  type CaptionTemplatePreviewAssetRun,
} from "~/modules/caption-templates/schemas";

export const Route = createFileRoute("/_dash/caption-templates/$templateId")({
  validateSearch: z.object({
    audience: captionTemplateAudienceSchema.default("all"),
    category: captionTemplateCategorySchema.default("all"),
    q: z.string().optional().catch(undefined),
    status: captionTemplateStatusFilterSchema.default("published"),
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const { templateId } = Route.useParams();
  const { audience, category, q, status } = Route.useSearch();
  const filterParams = getCaptionTemplateFilterParams({ audience, status });

  const {
    data: template,
    error,
    isLoading,
  } = captionTemplateMetadataQuery.useQuery(
    {
      pathParams: {
        templateId,
      },
      params: filterParams,
    },
    {
      refetchInterval: (query) => {
        const data = query.state.data as CaptionTemplateMetadata | undefined;

        return data?.latestPreviewAssetRun?.status === "running" ? 5_000 : false;
      },
      staleTime: Infinity,
    }
  );

  return (
    <div className="space-y-6 pb-12">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="outline" size="sm" asChild className="gap-1.5">
          <Link to="/caption-templates" search={{ audience, category, q, status }}>
            <ArrowLeft className="size-4" aria-hidden="true" />
            Templates
          </Link>
        </Button>
      </div>

      {isLoading && <CaptionTemplateDetailSkeleton />}

      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error instanceof Error ? error.message : "Failed to load caption template"}
        </div>
      )}

      {template && <CaptionTemplateDetail template={template} />}
    </div>
  );
}

function CaptionTemplateDetail({ template }: { template: CaptionTemplateMetadata }) {
  const startPreviewAssets = startCaptionTemplatePreviewAssetsMutation.useMutation({
    onSuccess: (response) => {
      if (response.run.status === "running") {
        toast.success("Preview asset generation started");
      }
    },
    onError: () => {
      toast.error("Failed to start preview asset generation");
    },
  });
  const latestRun = template.latestPreviewAssetRun ?? null;
  const isGenerating = latestRun?.status === "running" || startPreviewAssets.isPending;

  const generatePreviewAssets = () => {
    startPreviewAssets.mutate({
      pathParams: { templateId: template.id },
      params: { overwrite: true },
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-xl font-bold">{template.name}</h1>
          <div className="mt-1 flex min-w-0 items-center gap-1.5">
            <span className="truncate font-mono text-xs text-muted-foreground" title={template.id}>
              {template.id}
            </span>
            <CopyTemplateIdButton templateId={template.id} />
            <CaptionTemplateStatusBadge status={template.status} />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <MetadataPill label="Status" value={template.status} />
            <MetadataPill label="Definition version" value={template.definitionVersion} />
            {template.renderingEngineVersion && (
              <MetadataPill label="Rendering engine" value={template.renderingEngineVersion} />
            )}
            {template.rank != null && <MetadataPill label="Rank" value={String(template.rank)} />}
            {template.numViews != null && (
              <MetadataPill label="Views" value={String(template.numViews)} />
            )}
            {latestRun && <PreviewAssetRunPill run={latestRun} />}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex min-h-6 flex-wrap items-center gap-1.5">
            <CaptionTemplateColors template={template} />
          </div>
          <Button
            type="button"
            size="sm"
            onClick={generatePreviewAssets}
            disabled={isGenerating}
            className="gap-1.5"
          >
            {isGenerating ? (
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            ) : (
              <RefreshCw className="size-4" aria-hidden="true" />
            )}
            {isGenerating ? "Generating" : "Generate preview assets"}
          </Button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <MetadataTagPanel
          title="Audience"
          values={getUniqueValues(
            template.audienceSegments.map(formatCaptionTemplateAudienceSegment)
          )}
        />
        <MetadataTagPanel title="Categories" values={getUniqueValues(template.categories)} />
      </div>

      <CaptionTemplateEditorPanel template={template} />

      <div className="grid gap-6 lg:grid-cols-2">
        <TemplateAssetPanel
          title="Preview video"
          url={template.previewVideoUrl ?? null}
          emptyLabel="No preview video"
          media={<TemplatePreviewVideo template={template} />}
        />
        <TemplateAssetPanel
          title="Thumbnail"
          url={template.thumbnailUrl ?? null}
          emptyLabel="No thumbnail"
          media={<TemplateThumbnailImage template={template} />}
        />
      </div>
    </div>
  );
}

function MetadataTagPanel({ title, values }: { title: string; values: string[] }) {
  return (
    <section className="space-y-2">
      <h2 className="text-sm font-semibold">{title}</h2>
      {values.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {values.map((value) => (
            <span
              key={value}
              className="rounded-md border bg-background px-2 py-1 font-mono text-xs text-muted-foreground"
            >
              {value}
            </span>
          ))}
        </div>
      ) : (
        <div className="text-sm text-muted-foreground">None</div>
      )}
    </section>
  );
}

function getUniqueValues(values: string[]) {
  return Array.from(new Set(values));
}

function MetadataPill({ label, value }: { label: string; value: string }) {
  return (
    <span className="rounded-md border bg-background px-2 py-1">
      <span className="font-medium text-foreground">{label}:</span> {value}
    </span>
  );
}

function PreviewAssetRunPill({ run }: { run: CaptionTemplatePreviewAssetRun }) {
  const runStatus = run.status.replaceAll("_", " ");
  return <MetadataPill label="Preview asset run" value={runStatus} />;
}

function TemplateAssetPanel({
  emptyLabel,
  media,
  title,
  url,
}: {
  emptyLabel: string;
  media: ReactNode;
  title: string;
  url: string | null;
}) {
  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold">{title}</h2>
      <div className="overflow-hidden rounded-md border bg-muted">
        {url ? media : <EmptyAsset label={emptyLabel} />}
      </div>
    </section>
  );
}

function TemplatePreviewVideo({ template }: { template: CaptionTemplateMetadata }) {
  const [hasError, setHasError] = useState(false);

  if (!template.previewVideoUrl) {
    return null;
  }

  if (hasError) {
    return <EmptyAsset label="Preview video failed to load" />;
  }

  return (
    <video
      src={template.previewVideoUrl}
      poster={template.thumbnailUrl ?? undefined}
      className="aspect-video w-full object-contain"
      controls
      playsInline
      preload="metadata"
      onError={() => setHasError(true)}
    />
  );
}

function TemplateThumbnailImage({ template }: { template: CaptionTemplateMetadata }) {
  const [hasError, setHasError] = useState(false);

  if (!template.thumbnailUrl) {
    return null;
  }

  if (hasError) {
    return <EmptyAsset label="Thumbnail failed to load" />;
  }

  return (
    <img
      src={template.thumbnailUrl}
      alt=""
      className="aspect-video w-full object-contain"
      onError={() => setHasError(true)}
    />
  );
}

function EmptyAsset({ label }: { label: string }) {
  return (
    <div className="flex aspect-video w-full items-center justify-center text-sm text-muted-foreground">
      {label}
    </div>
  );
}

function CaptionTemplateDetailSkeleton() {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Skeleton className="h-7 w-56" />
        <Skeleton className="h-4 w-80" />
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <Skeleton className="aspect-video rounded-md" />
        <Skeleton className="aspect-video rounded-md" />
      </div>
    </div>
  );
}
